import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { ft } from "./api";
import { getAttribution } from "../lib/attribution";

const AuthCtx = createContext(null);

// Fire-and-forget attach helper reused by every auth path.
// Failure here must NEVER block the auth flow.
async function _safeAttachAttribution() {
    try {
        const snap = getAttribution();
        if (snap.first || snap.last) await ft.attachAttribution(snap);
    } catch (_e) { /* silent */ }
}

export function AppAuthProvider({ children }) {
    const [user, setUser]   = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const refresh = useCallback(async () => {
        try {
            const me = await ft.me();
            setUser(me);
        } catch (e) {
            setUser(null);
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    const login = async (email, password) => {
        setError(null);
        await ft.login(email, password);
        await refresh();
        _safeAttachAttribution();
    };
    const register = async (payload) => {
        setError(null);
        await ft.register(payload);
        await refresh();
        _safeAttachAttribution();
    };
    const phoneSendOtp = async (phone) => {
        setError(null);
        return await ft.phoneSendOtp(phone); // {ok:true, expires_in_seconds}
    };
    const phoneVerifyOtp = async (phone, code) => {
        setError(null);
        const r = await ft.phoneVerifyOtp(phone, code);
        if (r?.verified === false) return r;      // wrong code — no session set
        await refresh();                          // success — fc_sid is now on the browser
        _safeAttachAttribution();
        return r;
    };
    const firebasePhoneVerify = async (idToken, extras) => {
        setError(null);
        const r = await ft.firebasePhoneVerify(idToken, extras);
        await refresh();                          // fc_sid now on the browser
        _safeAttachAttribution();
        return r;
    };
    const logout = async () => {
        await ft.logout();
        setUser(null);
    };

    return (
        <AuthCtx.Provider value={{ user, loading, error, login, register, phoneSendOtp, phoneVerifyOtp, firebasePhoneVerify, logout, refresh }}>
            {children}
        </AuthCtx.Provider>
    );
}

export function useAppAuth() {
    const v = useContext(AuthCtx);
    if (!v) throw new Error("useAppAuth must be used inside <AppAuthProvider>");
    return v;
}
